import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { 
  LayoutDashboard, 
  PlusCircle, 
  FileText, 
  BarChart3, 
  Settings, 
  LogOut,
  Building2,
  TrendingUp,
  Menu,
  X,
  Package,
  Calendar,
  Upload,
  ClipboardList,
  DollarSign,
  Users,
  History
} from 'lucide-react';
import { Button } from './ui/button';
import { useState } from 'react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_showtime-metrics/artifacts/rku0gqo6_logo%20jf.png';

export const Sidebar = () => {
  const { user, logout, isExecutive } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const managerLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/entry', icon: PlusCircle, label: 'New Entry' },
    { to: '/daily-scorecard', icon: ClipboardList, label: 'Daily Scorecard' },
    { to: '/scorecards', icon: FileText, label: 'My Scorecards' },
    { to: '/trends', icon: TrendingUp, label: 'Trends' },
  ];

  const executiveLinks = [
    { to: '/executive', icon: LayoutDashboard, label: 'Overview' },
    { to: '/daily-scorecard', icon: ClipboardList, label: 'Daily Scorecard' },
    { to: '/rankings', icon: TrendingUp, label: 'Rankings' },
    { to: '/compare', icon: BarChart3, label: 'Compare' },
    { to: '/weekly-report', icon: Calendar, label: 'Weekly Report' },
    { to: '/budgets', icon: DollarSign, label: 'Budgets' },
    { to: '/pax-import', icon: Upload, label: 'PAX Import' },
    { to: '/users', icon: Users, label: 'Users' },
    { to: '/audit-log', icon: History, label: 'Audit Log' },
    { to: '/locations', icon: Building2, label: 'Locations' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  const links = isExecutive ? executiveLinks : managerLinks;

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-md border border-slate-200"
        data-testid="mobile-menu-toggle"
      >
        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/20 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        'sidebar fixed lg:sticky top-0 left-0 z-40 h-screen flex flex-col bg-white border-r border-slate-200',
        'transition-transform duration-300 lg:translate-x-0',
        isOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        {/* Logo */}
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Showtime" className="h-10 w-auto" />
            <div>
              <h1 className="font-bold text-slate-900 font-['Outfit']">STPScorecard</h1>
              <span className="text-xs text-slate-500">Showtime Pictures</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {links.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setIsOpen(false)}
              className={({ isActive }) => cn(
                'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors',
                isActive 
                  ? 'bg-violet-100 text-violet-700' 
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              )}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-100">
          <div className="px-4 py-3 rounded-xl bg-slate-50 mb-3">
            <p className="font-medium text-slate-900 text-sm">{user?.name}</p>
            <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-slate-600 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </Button>
        </div>
      </aside>
    </>
  );
};
